// Página de Propostas (Comercial): lista e vista por fase, com o botão Nova proposta.
// Só lê os dados que a app já tem; abrir uma proposta usa o detalhe que já existe.
import { useState, useMemo } from "react";
import { FileText, Plus, Search, ChevronRight, Calendar, Clock, Handshake, CheckCircle2, XCircle, ArrowUp, ArrowDown, Download } from "lucide-react";
import { paleta } from "./tokens";

const GRADIENTE = "linear-gradient(90deg, #0B1B3D 0%, #1E3566 45%, #4A6BA8 100%)";
const CINZA_ACT = "#6B7385";

const FASES = [
  { id: "pendente", rotulo: "Pendente", estados: ["pendente"], Icone: Clock },
  { id: "negociacao", rotulo: "Em negociação", estados: ["negociacao"], Icone: Handshake },
  { id: "aceite", rotulo: "Aceite", estados: ["aceite"], Icone: CheckCircle2 },
  { id: "recusada", rotulo: "Recusada ou cancelada", estados: ["recusada", "recusada_neg", "cancelada"], Icone: XCircle },
];
const ROTULO_ESTADO = { pendente: "Pendente", negociacao: "Em negociação", aceite: "Aceite", recusada: "Recusada", recusada_neg: "Recusada", cancelada: "Cancelada" };

const COLUNAS = "120px minmax(130px, 1.3fr) minmax(110px, 1fr) 110px 105px 105px minmax(100px, 1fr) 85px 55px 130px";
const CABECALHO = [
  { t: "Referência", col: "ref", dir: "left" }, { t: "Comprador", col: "comprador", dir: "left" }, { t: "Imóvel", col: "imovel", dir: "left" },
  { t: "Estado", col: "estado", dir: "left" }, { t: "Proposto", col: "proposto", dir: "right" }, { t: "Aceite", col: "aceite", dir: "right" },
  { t: "Consultor", col: "consultor", dir: "left" }, { t: "Data", col: "data", dir: "left" }, { t: "Visitas" }, { t: "Fase", col: "fase", dir: "left" },
];
const eur = (v) => (v ? Number(v).toLocaleString("pt-PT", { maximumFractionDigits: 0 }) + " €" : "—");
const dataPT = (d) => (d ? new Date(d).toLocaleDateString("pt-PT") : "—");
const faseDe = (estado) => (FASES.find(f => f.estados.includes(estado)) || FASES[0]).id;

function Pilula({ ativo, onClick, children }) {
  return <button onClick={onClick} aria-pressed={ativo} style={{ background: ativo ? CINZA_ACT : "#fff", color: ativo ? "#fff" : paleta.navy900, border: "1px solid " + (ativo ? CINZA_ACT : paleta.cinza200), borderRadius: 99, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>{children}</button>;
}

function Cartao({ Icone, titulo, valor, nota, ativo, onClick }) {
  return (
    <button onClick={onClick} aria-pressed={!!ativo} style={{ textAlign: "left", background: "#fff", border: "1px solid " + (ativo ? paleta.navy700 : paleta.cinza200), borderTop: "3px solid " + (ativo ? paleta.navy700 : paleta.cinza200), borderRadius: 12, padding: "12px 14px", cursor: "pointer", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: paleta.cinza600, fontWeight: 500 }}><Icone size={15} aria-hidden="true" />{titulo}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: paleta.navy900, marginTop: 2 }}>{valor}</div>
      {nota && <div style={{ fontSize: 12, color: paleta.cinza600 }}>{nota}</div>}
    </button>
  );
}

export default function PainelPropostas({ propostas = [], leads = [], negocios = [], visitas = [], isLider = false, concluidos = [], onNova, onAbrir, onExportar }) {
  const [vista, setVista] = useState("lista"); // "lista" | "fase"
  const [fase, setFase] = useState("todas");
  const [periodo, setPeriodo] = useState("todo");
  const [consultor, setConsultor] = useState("");
  const [pesquisa, setPesquisa] = useState("");
  const [ordem, setOrdem] = useState({ col: "data", dir: "desc" });

  const leadPorId = useMemo(() => { const m = {}; (leads || []).forEach(l => { m[String(l.id)] = l; }); return m; }, [leads]);
  const negPorProposta = useMemo(() => { const m = {}; (negocios || []).forEach(n => { if (n.proposta_id && !n.deleted_at) m[String(n.proposta_id)] = n; }); return m; }, [negocios]);
  const visitasPorRef = useMemo(() => {
    const m = {};
    (visitas || []).forEach(v => { const r = v.id_imovel && String(v.id_imovel).trim(); if (r) { (m[r] = m[r] || []).push(v); } });
    return m;
  }, [visitas]);

  const ano = new Date().getFullYear();
  const mes = new Date().getMonth();
  const noPeriodo = (d) => {
    if (periodo === "todo") return true;
    if (!d) return false;
    const x = new Date(d);
    if (periodo === "mes") return x.getFullYear() === ano && x.getMonth() === mes;
    if (periodo === "mesAnt") { const a = mes === 0 ? ano - 1 : ano; const m = mes === 0 ? 11 : mes - 1; return x.getFullYear() === a && x.getMonth() === m; }
    if (periodo === "ano") return x.getFullYear() === ano;
    return true;
  };
  const nomeConsultor = (p) => p.consultor_venda_nome || p.consultor_ang_nome || "";
  const consultores = useMemo(() => Array.from(new Set((propostas || []).map(nomeConsultor).filter(Boolean))).sort((a, b) => a.localeCompare(b, "pt")), [propostas]);
  const nomeComprador = (p) => (leadPorId[String(p.lead_comprador_id)] || {}).nome || p.comprador_ext_colega || "—";

  const base = useMemo(() => {
    const q = pesquisa.trim().toLowerCase();
    return (propostas || []).filter(p => {
      if (!noPeriodo(p.created_at)) return false;
      if (consultor && nomeConsultor(p) !== consultor) return false;
      if (q) {
        const alvo = [p.imovel_ref, nomeComprador(p), p.imovel_morada, p.imovel_concelho].filter(Boolean).join(" ").toLowerCase();
        if (!alvo.includes(q)) return false;
      }
      return true;
    });
  }, [propostas, periodo, consultor, pesquisa, leadPorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const porFase = useMemo(() => {
    const m = { pendente: [], negociacao: [], aceite: [], recusada: [] };
    base.forEach(p => { m[faseDe(p.estado)].push(p); });
    return m;
  }, [base]);

  const somaValor = (lista) => lista.reduce((t, p) => t + Number(p.valor || p.valor_proposto || 0), 0);
  const faseNegocio = (p) => {
    const n = negPorProposta[String(p.id)];
    if (!n) return p.estado === "aceite" ? "Sem negócio" : "";
    const t = String(n.estado || "").replace(/_/g, " ");
    return t.charAt(0).toUpperCase() + t.slice(1);
  };
  const valorOrdem = (p, col) => {
    if (col === "ref") return String(p.imovel_ref || "");
    if (col === "comprador") return nomeComprador(p).toLowerCase();
    if (col === "imovel") return [p.imovel_tipo, p.imovel_tipologia].filter(Boolean).join(" ").toLowerCase();
    if (col === "estado") return ROTULO_ESTADO[p.estado] || p.estado || "";
    if (col === "proposto") return Number(p.valor_proposto || 0);
    if (col === "aceite") return Number(p.valor || 0);
    if (col === "consultor") return nomeConsultor(p).toLowerCase();
    if (col === "fase") return faseNegocio(p).toLowerCase();
    return String(p.created_at || "");
  };
  const filtradas = base
    .filter(p => fase === "todas" || faseDe(p.estado) === fase)
    .slice().sort((a, b) => {
      const x = valorOrdem(a, ordem.col), y = valorOrdem(b, ordem.col);
      const r = typeof x === "number" ? x - y : String(x).localeCompare(String(y), "pt");
      return ordem.dir === "asc" ? r : -r;
    });
  const ordenarPor = (col) => setOrdem(o => (o.col === col ? { col, dir: o.dir === "asc" ? "desc" : "asc" } : { col, dir: col === "data" || col === "proposto" || col === "aceite" ? "desc" : "asc" }));
  const exportar = () => onExportar && onExportar(filtradas.map(p => ({
    Ref: p.imovel_ref || "", Comprador: nomeComprador(p), Imóvel: [p.imovel_tipo, p.imovel_tipologia].filter(Boolean).join(" "),
    Estado: ROTULO_ESTADO[p.estado] || p.estado || "", "Valor proposto": Number(p.valor_proposto || 0), "Valor aceite": Number(p.valor || 0),
    Consultor: nomeConsultor(p), Data: p.created_at ? new Date(p.created_at).toLocaleDateString("pt-PT") : "", Fase: faseNegocio(p),
  })));

  const chipVisitas = (p) => {
    const vs = visitasPorRef[String(p.imovel_ref || "").trim()] || [];
    if (!vs.length) return null;
    const real = vs.filter(v => v.estado === "realizada").length;
    const tip = `${vs.length} ${vs.length === 1 ? "visita" : "visitas"} a este imóvel (${real} ${real === 1 ? "realizada" : "realizadas"})`;
    return <span title={tip} aria-label={tip} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: paleta.navy50, border: "1px solid " + paleta.navy100, color: paleta.navy900, borderRadius: 99, padding: "1px 8px", fontSize: 12, fontWeight: 600 }}><Calendar size={12} aria-hidden="true" />{vs.length}</span>;
  };
  const concluida = (p) => { const n = negPorProposta[String(p.id)]; return !!n && concluidos.includes(n.estado); };
  const select = { padding: "7px 10px", border: "1px solid " + paleta.cinza200, borderRadius: 8, fontSize: 13.5, background: "#fff", color: paleta.cinza900, fontFamily: "inherit" };

  return (
    <div>
      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: paleta.navy900, margin: 0 }}>Propostas</h1>
          <div style={{ fontSize: 13.5, color: paleta.cinza600, marginTop: 2 }}>{base.length} {base.length === 1 ? "proposta" : "propostas"}. {isLider ? "Vês as propostas de toda a equipa." : "Vês as tuas propostas."}</div>
        </div>
        {onNova && <button onClick={onNova} style={{ background: "#1E3566", color: "#fff", border: "1px solid #1E3566", borderRadius: 8, padding: "9px 18px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}><Plus size={16} aria-hidden="true" />Nova proposta</button>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 14 }}>
        {FASES.map(f => (
          <Cartao key={f.id} Icone={f.Icone} titulo={f.rotulo} valor={porFase[f.id].length} nota={eur(somaValor(porFase[f.id]))} ativo={fase === f.id} onClick={() => setFase(fase === f.id ? "todas" : f.id)} />
        ))}
      </div>

      <div style={{ background: "#fff", border: "1px solid " + paleta.cinza200, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ background: GRADIENTE, color: "#fff", padding: "10px 16px", fontSize: 15.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
          <FileText size={17} aria-hidden="true" />{vista === "lista" ? `Lista (${filtradas.length})` : "Por fase"}
        </div>
        <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10, borderBottom: "1px solid " + paleta.navy100 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 6 }}>
              <Pilula ativo={vista === "lista"} onClick={() => setVista("lista")}>Lista</Pilula>
              <Pilula ativo={vista === "fase"} onClick={() => setVista("fase")}>Por fase</Pilula>
            </div>
            <div style={{ position: "relative", flex: "1 1 200px", minWidth: 180 }}>
              <Search size={15} aria-hidden="true" style={{ position: "absolute", left: 10, top: 10, color: paleta.cinza400 }} />
              <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Pesquisar imóvel ou comprador" aria-label="Pesquisar propostas" style={{ ...select, width: "100%", paddingLeft: 32, boxSizing: "border-box" }} />
            </div>
            <select value={periodo} onChange={e => setPeriodo(e.target.value)} aria-label="Período" style={select}>
              <option value="todo">Todo o período</option><option value="mes">Este mês</option><option value="mesAnt">Mês passado</option><option value="ano">Este ano</option>
            </select>
            {onExportar && vista === "lista" && <button onClick={exportar} style={{ padding: "7px 12px", border: "1px solid " + paleta.cinza200, borderRadius: 8, fontSize: 13.5, fontWeight: 600, background: "#fff", color: paleta.navy900, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}><Download size={15} aria-hidden="true" />Exportar Excel</button>}
            {isLider && consultores.length > 0 && (
              <select value={consultor} onChange={e => setConsultor(e.target.value)} aria-label="Consultor" style={select}>
                <option value="">Todos os consultores</option>{consultores.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
        </div>

        {vista === "lista" ? (
          filtradas.length === 0 ? (
            <div style={{ textAlign: "center", padding: 28, color: paleta.cinza400, fontSize: 14 }}>Nenhuma proposta neste filtro.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 1040 }}>
                <div style={{ display: "grid", gridTemplateColumns: COLUNAS, gap: 10, padding: "9px 14px", background: paleta.navy50, borderBottom: "1px solid " + paleta.navy100 }}>
                  {CABECALHO.map(c => c.col ? (
                    <button key={c.col} onClick={() => ordenarPor(c.col)} aria-label={"Ordenar por " + c.t} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: c.dir, fontSize: 12.5, fontWeight: 600, color: ordem.col === c.col ? paleta.navy900 : paleta.cinza600, display: "inline-flex", alignItems: "center", gap: 4, justifyContent: c.dir === "right" ? "flex-end" : "flex-start" }}>
                      {c.t}{ordem.col === c.col && (ordem.dir === "asc" ? <ArrowUp size={12} aria-hidden="true" /> : <ArrowDown size={12} aria-hidden="true" />)}
                    </button>
                  ) : <div key={c.t} />)}
                </div>
                {filtradas.map(p => {
                  const rec = faseDe(p.estado) === "recusada";
                  const cinza = { color: paleta.cinza600, fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
                  return (
                    <button key={p.id} onClick={() => onAbrir && onAbrir(p)} style={{ width: "100%", textAlign: "left", background: "#fff", border: "none", borderBottom: "1px solid " + paleta.navy100, padding: "11px 14px", display: "grid", gridTemplateColumns: COLUNAS, gap: 10, alignItems: "center", cursor: "pointer", opacity: rec ? 0.6 : 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5, color: paleta.cinza900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.imovel_ref || "Sem referência"}</div>
                      <div style={{ ...cinza, color: paleta.cinza900 }}>{nomeComprador(p)}</div>
                      <div style={cinza}>{[p.imovel_tipo, p.imovel_tipologia].filter(Boolean).join(" ") || "—"}</div>
                      <div><span style={{ fontSize: 12, fontWeight: 600, color: paleta.navy900, background: paleta.navy50, border: "1px solid " + paleta.navy100, borderRadius: 99, padding: "1px 9px", whiteSpace: "nowrap" }}>{ROTULO_ESTADO[p.estado] || p.estado}</span></div>
                      <div style={{ ...cinza, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{eur(p.valor_proposto)}</div>
                      <div style={{ textAlign: "right", fontSize: 13.5, fontWeight: 700, color: paleta.navy900, fontVariantNumeric: "tabular-nums" }}>{eur(p.valor)}</div>
                      <div style={cinza}>{nomeConsultor(p) || "—"}</div>
                      <div style={cinza}>{dataPT(p.created_at)}</div>
                      <div>{chipVisitas(p)}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                        {concluida(p) ? <span style={{ fontSize: 12, fontWeight: 600, color: paleta.sucesso, background: paleta.sucessoFundo, borderRadius: 99, padding: "1px 9px", whiteSpace: "nowrap" }}>Concluído</span> : <span style={{ ...cinza, fontSize: 12.5 }}>{faseNegocio(p) || "—"}</span>}
                        <ChevronRight size={15} aria-hidden="true" style={{ color: paleta.cinza400, marginLeft: "auto", flexShrink: 0 }} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 12, padding: 14, alignItems: "start" }}>
            {FASES.filter(f => fase === "todas" || fase === f.id).map(f => (
              <div key={f.id} style={{ background: paleta.navy50, border: "1px solid " + paleta.navy100, borderRadius: 12, overflow: "hidden" }}>
                <div style={{ background: GRADIENTE, color: "#fff", padding: "9px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                  <f.Icone size={16} aria-hidden="true" />
                  <span style={{ fontWeight: 600, fontSize: 14.5, flex: 1 }}>{f.rotulo}</span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{porFase[f.id].length}</span>
                </div>
                <div style={{ padding: "6px 14px", fontSize: 12.5, color: paleta.cinza600, borderBottom: "1px solid " + paleta.navy100 }}>Total {eur(somaValor(porFase[f.id]))}</div>
                <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8, maxHeight: "60vh", overflowY: "auto" }}>
                  {porFase[f.id].length === 0 ? <div style={{ textAlign: "center", padding: 14, color: paleta.cinza400, fontSize: 13 }}>Sem propostas.</div> : porFase[f.id].slice().sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))).map(p => (
                    <button key={p.id} onClick={() => onAbrir && onAbrir(p)} style={{ textAlign: "left", background: "#fff", border: "1px solid " + paleta.cinza200, borderRadius: 10, padding: "10px 12px", cursor: "pointer" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                        <span style={{ fontWeight: 600, fontSize: 13.5, color: paleta.cinza900 }}>{p.imovel_ref || "Sem referência"}</span>
                        {chipVisitas(p)}
                      </div>
                      <div style={{ fontSize: 13, color: paleta.cinza600, marginTop: 2 }}>{nomeComprador(p)}</div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: paleta.navy900, fontVariantNumeric: "tabular-nums" }}>{eur(p.valor || p.valor_proposto)}</span>
                        <span style={{ fontSize: 12, color: paleta.cinza400 }}>{dataPT(p.created_at)}</span>
                      </div>
                      {nomeConsultor(p) && <div style={{ fontSize: 12, color: paleta.cinza400, marginTop: 2 }}>{nomeConsultor(p)}</div>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
